import { AssessmentStatus as Assessment_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-status';
import type { UpdateInternalAuditReportMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateInternalAuditReportDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type {
  UpdateInternalAuditReportInput,
  UpdateInternalAuditReportRequest,
} from './types';

export const useUpdateInternalAuditReport = createMutationHook({
  name: 'updateInternalAuditReport',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.internalAuditReport.update.mutationOptions({ onSuccess }),
  // Report has no schedule, so no schedule transformation is needed. The
  // legacy action returns affected_rows (NOT an IdOutput). Since COI-622 the
  // tRPC leg locks on OriginalTimestamp; the legacy mutation input has no such
  // field, so `graphqlVariables` strips it below.
  mapInput: ({
    OriginatingItemId: _legacyOnly,
    ...variables
  }: UpdateInternalAuditReportInput): UpdateInternalAuditReportRequest => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.NotStarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.internalAuditReport.register.queryKey(),
    trpc.frontend.internalAuditReport.internalAuditReportById.queryKey(),
    // COI-471 parity: Apollo twin also evicts `internal_audit_entity` and the
    // assessment-result fields (their ratings roll up into the report).
    trpc.frontend.internalAuditEntity.pathKey(),
    trpc.frontend.documentAssessmentResult.pathKey(),
    trpc.frontend.obligationAssessmentResult.pathKey(),
    trpc.frontend.riskAssessmentResult.pathKey(),
  ],
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateInternalAuditReportMutation => ({
    updateInternalAuditReportApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: UpdateInternalAuditReportDocument,
  // COI-622: strip the concurrency token — the legacy mutation input has no
  // OriginalTimestamp field, so only the tRPC leg locks on it.
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateInternalAuditReportInput) => ({
    object: variables,
  }),
  evictFields: [
    'risk_assessment_result',
    'document_assessment_result',
    'obligation_assessment_result',
    'internal_audit_entity',
    'internal_audit_report',
  ],
});
